// Industry detail content for /industries/:slug pages
// All copy original to BuyerBeacon Pro. Target market: SMB + Agencies.

export const industryDetails = {
  roofing: {
    slug: "roofing",
    name: "Roofing",
    icon: "Home",
    eyebrow: "For Roofing Contractors",
    tagline: "Find homeowners with damaged roofs — before they Google your competitor.",
    description: "Storms, age, and home-sale activity are the three biggest triggers for a new roof. BuyerBeacon Pro surfaces every homeowner in your service area showing those signals — and lets you reach them with door-hangers, postcards, or paid social before another contractor knocks.",
    painPoints: [
      "Storm chasers flood the market right after weather events",
      "Insurance-claim leads dry up between events",
      "Paying lead-gen companies $80–$200 per shared lead",
      "Cold canvassing wastes crew time"
    ],
    solutionStack: [
      { slug: "consumer-data", why: "Filter homeowners by roof age, home value, and ZIP — down to 5,000 doors at a time." },
      { slug: "buyer-intent", why: "Surface households searching for ‘roof leak,’ ‘storm damage,’ or ‘insurance claim help.’" },
      { slug: "offline-activation", why: "Trigger personalized door-hangers within 48 hours of any storm event." }
    ],
    signals: ["Home age 15+ years", "Recent storm in ZIP", "Homeowner status verified", "Mortgage age", "Insurance-claim search behavior", "Estimated home equity"],
    useCase: {
      title: "How a regional roofer doubled storm-response revenue",
      summary: "Within 24 hours of a hailstorm in their tri-county area, the contractor mailed 8,400 personalized door-hangers to homeowners with roofs older than 12 years. Booked 312 inspections in the next two weeks — of which 47 became signed contracts."
    }
  },

  windows: {
    slug: "windows",
    name: "Windows & Doors",
    icon: "AppWindow",
    eyebrow: "For Window Replacement Companies",
    tagline: "Old single-pane windows hide in plain sight. We help you find them.",
    description: "Window replacement is one of the highest-ticket home-improvement purchases — and one of the longest sales cycles. The right list, mailed at the right moment, beats every form-fill lead-gen platform on the market.",
    painPoints: [
      "$90–$300 cost-per-lead from shared lead aggregators",
      "Tire-kicker quote-shoppers wasting estimator time",
      "Long sales cycles with poor follow-up data",
      "Difficulty identifying homes that haven’t been renovated"
    ],
    solutionStack: [
      { slug: "consumer-data", why: "Homeowners with homes built before 1990 and incomes that support a $15K+ purchase." },
      { slug: "buyer-intent", why: "Households researching energy-efficient windows, tax credits, or home renovation financing." },
      { slug: "offline-activation", why: "Variable-data postcards highlighting the federal energy-efficient window tax credit." }
    ],
    signals: ["Home built pre-1990", "Homeowner", "Income $100K+", "Energy-efficiency intent", "Home-improvement loan inquiry", "Recent home purchase"],
    useCase: {
      title: "Window company replaces aggregator leads with first-party data",
      summary: "Cut $120 cost-per-lead to $14 by replacing a lead aggregator subscription with direct-mail to filtered homeowner audiences. Close rate doubled because the leads were exclusive."
    }
  },

  landscaping: {
    slug: "landscaping",
    name: "Landscaping",
    icon: "Trees",
    eyebrow: "For Landscape & Lawn-Care Companies",
    tagline: "Spring sign-ups should happen in February. Find the homeowners before competitors do.",
    description: "Lawn care is bought by routine — the homeowner who hires you in March will likely renew every season for years. The trick is being first in the mailbox in February. BuyerBeacon Pro builds that list.",
    painPoints: [
      "Door-to-door canvassing burns crew hours",
      "Yelp and Google Ads are crowded and expensive",
      "Hard to identify recently-moved homeowners (best customers)",
      "Seasonal demand spikes faster than ad campaigns can scale"
    ],
    solutionStack: [
      { slug: "consumer-data", why: "Homeowners with lots over 5,000 sq ft, recent move-ins, and household incomes that match your pricing." },
      { slug: "audience-builder", why: "Build neighborhood-by-neighborhood audiences and refresh annually." },
      { slug: "offline-activation", why: "Pre-spring postcards with neighborhood-specific imagery and early-bird pricing." }
    ],
    signals: ["Homeowner", "Lot size 5,000+ sq ft", "Move-in within 12 months", "Income $80K+", "Pool or patio present", "Pet ownership"],
    useCase: {
      title: "Local landscaper books summer in February",
      summary: "Mailed 6,200 postcards to recently-moved homeowners in their service ZIPs three weeks before competitors started spring campaigns. Locked in 184 annual contracts before March 1."
    }
  },

  "home-renovation": {
    slug: "home-renovation",
    name: "Home Renovation",
    icon: "Hammer",
    eyebrow: "For Remodelers & General Contractors",
    tagline: "Renovation budgets follow life events. We track the events.",
    description: "Home renovation is rarely a cold purchase — it follows a move, an inheritance, a job change, or a major life event. BuyerBeacon Pro surfaces those triggers in your service area and routes them to your sales team the day they fire.",
    painPoints: [
      "Long sales cycles with limited visibility into prospect timing",
      "Referral pipeline is unpredictable",
      "Difficulty reaching high-ticket buyers ($50K+ projects)",
      "Generic ads attract bargain-hunters"
    ],
    solutionStack: [
      { slug: "consumer-data", why: "Filter for income, home value, and life events like recent move, marriage, or baby." },
      { slug: "pulse", why: "Real-time alerts when watched households trigger renovation-related intent." },
      { slug: "buyer-intent", why: "Households researching kitchen remodels, bathroom additions, or basement finishing." }
    ],
    signals: ["Home value $400K+", "Recent move", "Renovation-loan inquiry", "Pinterest ‘home remodel’ activity", "Permit-filing in ZIP", "Income $150K+"],
    useCase: {
      title: "Custom remodeler triples its $100K+ project pipeline",
      summary: "Layered Pulse alerts on top of high-income recently-moved households. Sales team contacts each new alert within 24 hours — booking rate on those calls is 3.8x cold outreach."
    }
  },

  dispensary: {
    slug: "dispensary",
    name: "Dispensary",
    icon: "Leaf",
    eyebrow: "For Cannabis Dispensaries",
    tagline: "You can’t buy ads on most major platforms. Direct-to-mailbox still works.",
    description: "Most digital ad platforms restrict cannabis advertising. Direct mail and SMS to a verified, age-gated, opt-in audience remain the most effective customer-acquisition channels — and BuyerBeacon Pro builds that audience.",
    painPoints: [
      "Banned from Meta, Google, and TikTok advertising",
      "Hard to verify 21+ status before targeting",
      "Compliance landscape changes state-by-state",
      "Competition from delivery apps and home grows"
    ],
    solutionStack: [
      { slug: "consumer-data", why: "Age-verified 21+ adults within delivery radius, filtered by household income." },
      { slug: "offline-activation", why: "Direct-mail menus, BOGO postcards, and loyalty re-engagement — fully compliant." },
      { slug: "audience-builder", why: "Suppress existing customers and surface only net-new prospects each campaign." }
    ],
    signals: ["Verified 21+", "Within 10-mile delivery radius", "Disposable income $40K+", "Cannabis-friendly state", "Recent move into service area"],
    useCase: {
      title: "Multi-location dispensary fills three new stores with mailers",
      summary: "Mailed launch-week menus to 24,000 age-verified adults within five miles of each grand opening. Average 6.8% redemption rate — 80% above their industry benchmark."
    }
  },

  hvac: {
    slug: "hvac",
    name: "HVAC",
    icon: "Wind",
    eyebrow: "For HVAC Contractors",
    tagline: "Heat waves and cold snaps don’t wait for ad campaigns to scale.",
    description: "HVAC demand is reactive — a furnace fails, a heat pump dies, an AC stops cooling. The contractor who shows up in the mailbox the week the system was installed (15 years ago) wins. BuyerBeacon Pro builds that list.",
    painPoints: [
      "Aggregator leads cost $60–$150 and are shared with 3+ competitors",
      "Emergency calls go to whoever ranks first on Google",
      "Hard to predict which systems are aging out",
      "Maintenance plans are undersold"
    ],
    solutionStack: [
      { slug: "consumer-data", why: "Homeowners with homes built 10–20 years ago — the most likely to replace systems." },
      { slug: "buyer-intent", why: "Households researching ‘AC repair,’ ‘furnace replacement,’ or ‘heat pump cost.’" },
      { slug: "offline-activation", why: "Trigger postcards the week of any heat or cold snap in your service area." }
    ],
    signals: ["Home age 10–20 yrs", "Homeowner", "AC-repair intent", "Furnace-replacement intent", "Recent weather event", "Energy-bill increase intent"],
    useCase: {
      title: "HVAC contractor stops buying aggregator leads",
      summary: "Replaced a $4,500/month aggregator subscription with direct-mail to age-filtered homeowners. Lead cost dropped to $11; close rate doubled because leads were exclusive."
    }
  },

  plumbing: {
    slug: "plumbing",
    name: "Plumbing",
    icon: "Droplets",
    eyebrow: "For Plumbing Companies",
    tagline: "Be the first name a homeowner sees when a pipe bursts.",
    description: "Most plumbing purchases are emergencies. The contractor with consistent local brand presence — fridge magnets, mailers, neighborhood door-hangers — wins those calls. BuyerBeacon Pro builds repeatable, geo-targeted local campaigns at a fraction of mass-market cost.",
    painPoints: [
      "Emergency calls go to the first Google result, not the best plumber",
      "Service-area boundaries waste mass-media spend",
      "Hard to surface high-value re-pipe and water-heater jobs",
      "Reviews matter — but reaching new homeowners to leave them is hard"
    ],
    solutionStack: [
      { slug: "consumer-data", why: "Filter by homeowner, home age, and exact service ZIPs." },
      { slug: "offline-activation", why: "Quarterly fridge-magnet mailers to every homeowner in your radius." },
      { slug: "buyer-intent", why: "Households searching ‘water heater replacement,’ ‘sewer line repair,’ or ‘re-pipe cost.’" }
    ],
    signals: ["Homeowner", "Home age 20+", "Recent move-in", "Plumbing-related search intent", "Water-heater age estimate", "ZIP within service area"],
    useCase: {
      title: "Family plumber doubles re-pipe revenue",
      summary: "Mailed 12,000 magnets to homes built before 1985 in three target ZIPs. Six months later, 38 re-pipe jobs (avg ticket $9,400) traced directly back to magnet calls."
    }
  },

  catering: {
    slug: "catering",
    name: "Catering",
    icon: "UtensilsCrossed",
    eyebrow: "For Catering Companies",
    tagline: "Weddings, corporate events, and life milestones — all knowable in advance.",
    description: "Catering bookings follow predictable events: engagements, corporate growth, anniversaries, milestone birthdays. BuyerBeacon Pro surfaces households and businesses showing those signals in your service area.",
    painPoints: [
      "Heavy reliance on referrals — hard to scale",
      "Wedding venues control most introductions",
      "Corporate accounts are won by whoever called first",
      "Inbound inquiries are price-shoppers, not qualified buyers"
    ],
    solutionStack: [
      { slug: "consumer-data", why: "Recently engaged couples, milestone birthdays, and high-income households planning events." },
      { slug: "business-intelligence", why: "Local companies with 50–500 employees — prime corporate-catering targets." },
      { slug: "buyer-intent", why: "Surface couples researching wedding venues, corporate event planners, or birthday packages." }
    ],
    signals: ["Recently engaged", "Milestone birthday upcoming", "Income $100K+", "Local business 50–500 employees", "Recent funding round", "Office expansion intent"],
    useCase: {
      title: "Catering company books $180K in corporate accounts in one quarter",
      summary: "Targeted 2,400 local mid-size companies showing recent hiring spikes. Direct-mail introduction kit landed three signed corporate-catering contracts averaging $60K/year."
    }
  },

  "car-dealership": {
    slug: "car-dealership",
    name: "Car Dealership",
    icon: "Car",
    eyebrow: "For New & Used Car Dealers",
    tagline: "Buyers shop online for weeks before stepping on a lot. Reach them at week one.",
    description: "Auto shoppers research for an average of 96 days before purchase — mostly anonymously. BuyerBeacon Pro identifies these shoppers in your DMA the moment they begin researching, so your dealership shows up before the competition.",
    painPoints: [
      "Hard to identify in-market shoppers before they walk in",
      "Third-party lead sites resell the same lead 5+ times",
      "Service department customers underleveraged for new-vehicle pitches",
      "Hard to retarget lost-deal shoppers"
    ],
    solutionStack: [
      { slug: "website-visitor-id", why: "Identify the 90% of inventory-page visitors who don’t fill out a form." },
      { slug: "buyer-intent", why: "Surface households researching specific makes, models, or trade-in values." },
      { slug: "offline-activation", why: "Mailers triggered by anonymous inventory browsing or lease-end timing." }
    ],
    signals: ["Vehicle research intent", "Lease-end within 90 days", "Anonymous inventory browse", "Income $75K+", "Recent income increase", "Family-size change"],
    useCase: {
      title: "Dealership recovers 24% of website abandoners",
      summary: "Installed Visitor ID on inventory pages. Identified 1,840 shoppers per month. Trigger-mailed personalized offers to 70%; closed 5.2% within 30 days."
    }
  },

  "wedding-venue": {
    slug: "wedding-venue",
    name: "Wedding Venue",
    icon: "HeartHandshake",
    eyebrow: "For Wedding Venues & Planners",
    tagline: "Engagement is a signal. Most couples shop venues within 30 days of getting engaged.",
    description: "Wedding venues book 12–18 months out, but the decision window opens the day a couple gets engaged. BuyerBeacon Pro surfaces newly-engaged couples in your service area before they walk a competitor’s grounds.",
    painPoints: [
      "Heavy dependence on bridal-show foot traffic",
      "Generic wedding directories cost thousands per month",
      "Hard to filter price-shoppers from real prospects",
      "Off-season inquiries scarce"
    ],
    solutionStack: [
      { slug: "consumer-data", why: "Newly-engaged couples within driving distance of your venue, filtered by income." },
      { slug: "offline-activation", why: "Beautiful printed venue brochures mailed within 30 days of engagement." },
      { slug: "pulse", why: "Real-time alerts when engaged couples in your market trigger venue-search intent." }
    ],
    signals: ["Recent engagement", "Combined income $120K+", "Within 60 miles of venue", "Wedding-Pinterest activity", "Venue-search intent", "Bridal magazine subscription"],
    useCase: {
      title: "Boutique venue books 14 weddings off one mailer",
      summary: "Mailed 1,800 venue brochures to engaged couples within an hour’s drive. Booked 14 weddings averaging $32,000 each over the next twelve months."
    }
  },

  "auto-repair": {
    slug: "auto-repair",
    name: "Auto Repair",
    icon: "Cog",
    eyebrow: "For Independent Auto Shops",
    tagline: "Earn the customer when their warranty expires and the dealer becomes optional.",
    description: "Vehicle owners stay with the dealer service department through the warranty period — then become winnable. BuyerBeacon Pro identifies vehicle owners in your area whose warranties have just lapsed, the highest-intent prospect group in the entire auto-repair market.",
    painPoints: [
      "Dealer service departments dominate the warranty years",
      "Yelp and Google are crowded with discount-driven competitors",
      "Hard to identify vehicles 4–7 years old (post-warranty sweet spot)",
      "One-time repair customers rarely come back"
    ],
    solutionStack: [
      { slug: "consumer-data", why: "Vehicle owners with vehicles 4–7 years old, geo-targeted to your service radius." },
      { slug: "offline-activation", why: "‘Welcome to the family’ postcards with a first-service discount." },
      { slug: "buyer-intent", why: "Surface owners researching specific repairs — transmission, brakes, AC — in your area." }
    ],
    signals: ["Vehicle age 4–7 yrs", "Out-of-warranty", "Within 10-mile service radius", "Specific repair intent", "Recent move-in", "Income $50K+"],
    useCase: {
      title: "Independent shop builds a 1,200-customer book in 18 months",
      summary: "Mailed 18,000 postcards over six waves to out-of-warranty vehicle owners. Converted 4.3% to first-service appointments — of which 71% became repeat customers."
    }
  },

  agencies: {
    slug: "agencies",
    name: "Agencies",
    icon: "Briefcase",
    eyebrow: "For Marketing & Media Agencies",
    tagline: "Bring data-intelligence to every client — under your own brand.",
    description: "BuyerBeacon Pro’s Authority partner program lets your agency layer first-party data, intent signals, and direct-mail activation onto every client engagement — under your own brand, with margins you set. We also offer agency-services packages for social-media and email-campaign management built on top of the same data layer.",
    painPoints: [
      "Clients ask for performance without giving you data access",
      "Building a data stack in-house is expensive and slow",
      "Hard to differentiate from agencies offering the same Meta/Google services",
      "Retainer churn when results plateau"
    ],
    solutionStack: [
      { slug: "authorized-partner", why: "White-label the full platform under your agency brand." },
      { slug: "audience-builder", why: "Build per-client audiences in isolated workspaces." },
      { slug: "offline-activation", why: "Add direct-mail as a profitable channel without printing infrastructure." }
    ],
    signals: ["—"],
    useCase: {
      title: "Boutique agency adds data + direct-mail — ARR up 47%",
      summary: "Eight-person agency added BuyerBeacon Authority partnership. Within six months, every client engagement included audience-building and direct-mail campaigns priced as added monthly retainer. Total agency ARR up 47% with no headcount growth."
    },
    services: [
      { name: "Social-media management", desc: "Strategy, creative, posting, and paid — fueled by BuyerBeacon audience data." },
      { name: "Email campaign management", desc: "List building, segmentation, deliverability, and lifecycle automation." },
      { name: "Direct-mail campaigns", desc: "Done-for-you creative, print, postage, and attribution." },
      { name: "Full-funnel performance", desc: "Cross-channel orchestration with closed-loop attribution back to closed-won revenue." }
    ]
  }
};

export const allIndustrySlugs = Object.keys(industryDetails);
export const industryList = Object.values(industryDetails).map(({ slug, name, icon, tagline }) => ({ slug, name, icon, tagline }));
